import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiInstance from '../utils/axios';
import { useLanguage } from '../contexts/LanguageContext';

const BrandCard = ({ brand }) => {
  const brandName = brand.title_pt || brand.title_es || brand.title;
  
  return (
    <Link
      to={`/products?brand=${brand.slug}`}
      className="group flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-border/50 hover:border-primary/50 hover:shadow-lg transition-all duration-300"
    >
      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-2">
        {brand.image ? (
          <img
            src={brand.image}
            alt={brandName}
            className="w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
          />
        ) : (
          <div className="w-full h-full bg-muted/30 rounded-lg flex items-center justify-center">
            <span className="text-lg font-bold text-muted-foreground/60 group-hover:text-foreground transition-colors duration-300">
              {brandName?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>
      <span className="text-xs md:text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors duration-300 text-center line-clamp-1">
        {brandName}
      </span>
    </Link>
  );
};

const BrandCarousel = () => {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const MAX_VISIBLE = 10;

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiInstance.get('brands/?featured=true');
        let data = Array.isArray(response.data) ? response.data : (response.data.results || []);
        if ((!data || data.length === 0)) {
          try {
            const allRes = await apiInstance.get('brands/');
            data = Array.isArray(allRes.data) ? allRes.data : (allRes.data.results || []);
          } catch (err) {
            console.error('Error fetching fallback all brands:', err);
          }
        }
        setBrands(data || []);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  if (loading) {
    return (
      <section className="py-6 md:py-8">
        <div className="mx-auto max-w-7xl px-4">
          <div className="h-6 w-32 bg-muted rounded animate-pulse mb-6" />
          <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="aspect-square bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (brands.length === 0) {
    return null;
  }

  const visibleBrands = brands.slice(0, MAX_VISIBLE);
  const hasMore = brands.length > MAX_VISIBLE;

  return (
    <section className="py-6 md:py-8">
      <div className="mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg md:text-xl font-bold text-foreground">
            {t('topBrands') || 'Marcas Destacadas'}
          </h2>
          {hasMore && (
            <Link
              to="/brands"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
            >
              {t('showAll') || 'Ver todas'}
            </Link>
          )}
        </div>

        {/* Static Grid of Brands */}
        <div className="grid grid-cols-5 md:grid-cols-10 gap-3 md:gap-4">
          {visibleBrands.map((brand) => (
            <BrandCard key={brand.id} brand={brand} />
          ))}
        </div>

        {/* Show all button for mobile */}
        {hasMore && (
          <div className="flex justify-center mt-4 md:hidden">
            <Link
              to="/brands"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 px-4 py-2 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
            >
              {t('showAllBrands') || 'Ver todas las marcas'} (+{brands.length - MAX_VISIBLE})
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandCarousel;
